import { useMemo, type FC, type RefObject } from 'react';
import { _isNil, _notNil } from '@/littledash';

import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';

import Spreadsheet, { type SpreadsheetSettings } from '@/components/UI/SpreadSheet/Spreadsheet';
import {
  type MapAnimalRow,
  mapAnimalsColumnData,
  mapAnimalsSettings,
  createPlannedIdCellsConfig,
  processDataChange,
} from '@/components/Animals/MapAnimals.utils';

interface MapRandomizedAnimalsTableProps {
  mapData: Array<MapAnimalRow>;
  setMapData: (data: Array<MapAnimalRow>) => void;
  groups: Array<TreatmentGroup>;
  plannedAnimals: Array<PlannedAnimalDefinition>;
  innerRef: RefObject<HotTableRef | null>;
  setInvalid: (invalid: boolean) => void;
  onDataChange: (updatedData: Array<MapAnimalRow>) => void;
}

const MapRandomizedAnimalsTable: FC<MapRandomizedAnimalsTableProps> = ({
  mapData,
  setMapData,
  groups,
  plannedAnimals,
  innerRef,
  setInvalid,
  onDataChange,
}) => {
  const columnData = useMemo(() => {
    return mapAnimalsColumnData(groups, innerRef);
  }, [groups, innerRef]);

  const settings = useMemo<SpreadsheetSettings>(() => mapAnimalsSettings(columnData), [columnData]);

  const cells = useMemo(
    () => createPlannedIdCellsConfig(mapData, groups, plannedAnimals),
    [mapData, groups, plannedAnimals]
  );

  const handleAfterChange = (changes: Array<[number, string | number, unknown, unknown]> | null, source: string) => {
    if (_isNil(changes) || source === 'loadData') {
      return;
    }
    const hotInstance = innerRef.current?.hotInstance ?? null;
    if (_notNil(hotInstance)) {
      const updatedMapData = processDataChange(hotInstance, plannedAnimals);
      setMapData(updatedMapData);
      onDataChange(updatedMapData);
    }
  };

  return (
    <Spreadsheet
      data={mapData as unknown as Array<Record<string, unknown>>}
      settings={settings}
      innerRef={innerRef as RefObject<HotTableRef>}
      className="mapRandomisedAnimals"
      buttonClasses="absolute-sheet-buttons"
      setInvalid={setInvalid}
      afterChange={handleAfterChange}
      addCol={false}
      testPrefix="map-randomized-animals"
      cells={cells}
    />
  );
};

export default MapRandomizedAnimalsTable;
